import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
import io from 'socket.io-client';
import './TripTracking.css';

const GRAPHQL_URL = process.env.REACT_APP_PUBLIC_GRAPHQL_URL || '/graphql';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4001';
const TRIP_TIME_ZONE = 'Africa/Lagos';



// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

// Default center (will be updated with trip data)
const defaultCenter = {
  lat: 37.7749,
  lng: -122.4194
};

const TripTracking = ({ shareToken }) => {
  const [tripData, setTripData] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [directions, setDirections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!shareToken) return;

    // Fetch initial trip data
    fetchTripData();

    // Connect to WebSocket for real-time updates
    const socket = io(`${SOCKET_URL}/public-tracking`);
    socket.emit('startTracking', { shareToken });
    socket.on('locationUpdate', (data) => {
      setCurrentLocation({
        lat: data.lat,
        lng: data.lng
      });
    });
    socket.on('tripData', (data) => {
      if (data.status === 'success' && data.result) {
        setTripData(data.result);
      }
    });
    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });
    return () => {
      socket.emit('stopTracking');
      socket.disconnect();
    };
  }, [shareToken]);

  const fetchTripData = async () => {
    try {

      const query = `
        query GetPublicTripData($shareToken: String!) {
          getPublicTripData(shareToken: $shareToken) {
            status
            errorMessage
            result {
              shareToken
              isActive
              expiresAt
              viewCount
              booking { pickUpLocation
                dropOffLocation
                pickUpLat
                pickUpLng
                dropOffLat
                dropOffLng
                tripStatus
                tripStart
                tripEnd
                totalDuration
                vehicleNumber
                totalRideDistance
                estimatedTotalFare
                currency
                driverName
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables: { shareToken }
        })
      });

      const data = await response.json();

      if (data.data && data.data.getPublicTripData && data.data.getPublicTripData.status === 200) {
        const tripShareData = data.data.getPublicTripData.result;
        setTripData(tripShareData);

        // Set initial location as map center
        if (tripShareData.booking.pickUpLat && tripShareData.booking.pickUpLng) {
          setMapCenter({
            lat: parseFloat(tripShareData.booking.pickUpLat),
            lng: parseFloat(tripShareData.booking.pickUpLng)
          });
        }
      } else {
        setError(data.data?.getPublicTripData?.errorMessage || 'Trip not found');
      }
    } catch (err) {
      setError('Failed to load trip data');
    } finally {
      setLoading(false);
    }
  };

  const getDirections = (origin, destination) => {
    if (!window.google || !window.google.maps) {
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: new window.google.maps.LatLng(destination.lat, destination.lng),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error('Directions request failed due to ' + status);
        }
      }
    );
  };

  // Effect to handle directions when maps are loaded
  useEffect(() => {
    if (mapsLoaded && tripData && tripData.booking.pickUpLat && tripData.booking.dropOffLat) {
      const pickUp = {
        lat: parseFloat(tripData.booking.pickUpLat),
        lng: parseFloat(tripData.booking.pickUpLng)
      };
      const dropOff = {
        lat: parseFloat(tripData.booking.dropOffLat),
        lng: parseFloat(tripData.booking.dropOffLng)
      };
      getDirections(pickUp, dropOff);
    }
  }, [mapsLoaded, tripData]);

  if (loading) {
    return (
      <div className="trip-tracking-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-tracking-container">
        <div className="error-message">
          <h2>Trip Not Found</h2>
          <p>{error}</p>
          <p><small>Token: {shareToken}</small></p>
          <p><small>Check browser console for more details</small></p>
        </div>
      </div>
    );
  }

  const formatTime = timestamp => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleTimeString('en-NG', {
      timeZone: TRIP_TIME_ZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).toUpperCase();
  };

  const formatDate = timestamp => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString('en-US', {
      timeZone: TRIP_TIME_ZONE,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const pickupTime = formatTime(tripData?.booking?.tripStart);
  const estimatedDropOff = tripData?.booking?.tripStart
    && Number(tripData?.booking?.totalDuration) > 0
    ? new Date(
      new Date(tripData.booking.tripStart).getTime()
        + (Number(tripData.booking.totalDuration) * 60 * 1000),
    )
    : null;
  const dropOffTime = tripData?.booking?.tripStatus === 'completed'
    ? formatTime(tripData?.booking?.tripEnd)
    : formatTime(estimatedDropOff);
  const dropOffTimePrefix = tripData?.booking?.tripStatus === 'completed'
    ? ''
    : 'EST. ';
  const tripDate = formatDate(
    tripData?.booking?.tripStart || tripData?.booking?.tripEnd,
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return '#ffc107';
      case 'approved': return '#17a2b8';
      case 'started': return '#28a745';
      case 'completed': return '#6c757d';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;600&display=swap" rel="stylesheet" />
      <div style={{
        minHeight: '100vh',
        background: '#F5F5F5',
        fontFamily: 'Raleway, sans-serif',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '18px',
        display: 'flex',
        flexDirection: 'column',
        padding: '40px',
        maxWidth: '80%',
        margin: '0 auto',
      }}>
        <div style={{
          textAlign: 'center',
          position: 'relative',
          marginBottom: '40px',
        }}>
          <h1 style={{
            color: '#333',
            margin: '0 0 8px 0',
            fontSize: '28px',
            fontWeight: 600,
            fontFamily: 'Raleway, sans-serif',
            lineHeight: '34px',
          }}>Live Trip Tracking</h1>
          <div style={{
            color: '#666',
            fontSize: '14px',
            fontWeight: 400,
            marginBottom: '20px',
          }}>Trip Information</div>
        </div>

        <div style={{
          background: 'white',
          padding: '24px',
          marginBottom: '40px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            fontSize: '12px',
            color: '#666',
            lineHeight: '18px',
            fontFamily: 'Raleway, sans-serif',
          }}>
            {tripDate}
          </div>

          <div style={{ marginBottom: '30px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px', position: 'relative' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#34a853',
                marginRight: '16px',
                marginTop: '3px',
                zIndex: 2,
              }}></div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: 600,
                  lineHeight: '18px',
                  marginBottom: '4px',
                  fontFamily: 'Raleway, sans-serif',
                }}>PICKUP{pickupTime ? ` (${pickupTime})` : ''}</div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  lineHeight: '18px',
                  fontWeight: 400,
                  fontFamily: 'Raleway, sans-serif',
                }}>{tripData?.booking?.pickUpLocation}</div>
              </div>
            </div>

            {/* Línea vertical conectora */}
            <div style={{
              position: 'absolute',
              left: '5px',
              top: '15px',
              bottom: '15px',
              width: '2px',
              background: '#34a853',
              zIndex: 1,
            }}></div>

            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px', position: 'relative' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#34a853',
                marginRight: '16px',
                marginTop: '3px',
                zIndex: 2,
              }}></div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '12px',
                  color: '#333',
                  fontWeight: 600,
                  lineHeight: '18px',
                  marginBottom: '4px',
                  fontFamily: 'Raleway, sans-serif',
                }}>DROP-OFF{dropOffTime ? ` (${dropOffTimePrefix}${dropOffTime})` : ''}</div>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  lineHeight: '18px',
                  fontWeight: 400,
                  fontFamily: 'Raleway, sans-serif',
                }}>{tripData?.booking?.dropOffLocation}</div>
              </div>
            </div>
          </div>

          <div style={{
            marginTop: '30px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            width: '100%',
            gap: '40px'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '12px',
                color: '#333',
                fontWeight: 600,
                lineHeight: '18px',
                marginBottom: '4px',
                fontFamily: 'Raleway, sans-serif',
                textTransform: 'uppercase',
              }}>DRIVER</div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                lineHeight: '18px',
                fontWeight: 400,
                fontFamily: 'Raleway, sans-serif',
              }}>{tripData?.booking?.driverName}</div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '12px',
                color: '#333',
                fontWeight: 600,
                lineHeight: '18px',
                marginBottom: '4px',
                fontFamily: 'Raleway, sans-serif',
                textTransform: 'uppercase',
              }}>VEHICLE NUMBER</div>
              <div style={{
                fontSize: '12px',
                color: '#666',
                lineHeight: '18px',
                fontWeight: 400,
                fontFamily: 'Raleway, sans-serif',
              }}>{tripData?.booking?.vehicleNumber}</div>
            </div>
          </div>
        </div>

        <div style={{
          background: 'white',
          margin: '0 0 40px 0',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          height: '500px',
          width: '100%',
        }}>
          <LoadScript
            googleMapsApiKey="AIzaSyCoaQBHMw5_XUoW3TUdEIkQGkSjOytRLIA"
            onLoad={() => setMapsLoaded(true)}
            onError={() => { }}
          >
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={14}
            >
              {/* Pickup marker - always show where trip started */}
              {tripData?.booking?.pickUpLat && (
                <Marker
                  position={{
                    lat: tripData.booking.pickUpLat,
                    lng: tripData.booking.pickUpLng
                  }}
                  title="Pickup Location"
                  icon={{
                    url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                  }}
                />
              )}

              {/* Destination marker - shows planned destination for active trips, actual dropoff for completed trips */}
              {tripData?.booking?.dropOffLat && (
                <Marker
                  position={{
                    lat: tripData.booking.dropOffLat,
                    lng: tripData.booking.dropOffLng
                  }}
                  title={tripData.booking.tripStatus === 'completed' ? 'Final Dropoff Location' : 'Planned Destination'}
                  icon={{
                    url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                  }}
                />
              )}

              {/* Driver car icon - only show for active trips */}
              {currentLocation && tripData?.booking?.tripStatus !== 'completed' && (
                <Marker
                  position={currentLocation}
                  title="Driver Current Location"
                  icon={{
                    url: '/Icons/MapMarker/paze_car_marker.png',
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20)
                  }}
                />
              )}

              {/* Route directions */}
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: '#4285F4',
                      strokeWeight: 5
                    }
                  }}
                />
              )}
            </GoogleMap>
          </LoadScript>
        </div>

        {/* Download CTA — routes through /get.html for device detection + GA tracking */}
        <a
          href="https://paze-x.com/get.html?app=rider&utm_source=trip-share&utm_medium=web&utm_campaign=share-your-ride"
          style={{
            display: 'block',
            textDecoration: 'none',
            background: 'white',
            padding: '24px',
            marginTop: '40px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
          }}
        >
          <div style={{
            color: '#333',
            fontSize: '16px',
            fontWeight: 600,
            marginBottom: '4px',
            fontFamily: 'Raleway, sans-serif',
          }}>Ride with PazeX</div>
          <div style={{
            color: '#666',
            fontSize: '13px',
            marginBottom: '16px',
            fontFamily: 'Raleway, sans-serif',
          }}>Book safe, affordable rides right from your phone.</div>
          <span style={{
            display: 'inline-block',
            background: '#28a745',
            color: 'white',
            padding: '12px 28px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Raleway, sans-serif',
          }}>Download the Rider App</span>
        </a>

        <div style={{
          textAlign: 'center',
          background: 'transparent',
          padding: '20px 0',
          border: 'none',
        }}>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#666',
            lineHeight: '18px',
            fontWeight: 400,
          }}>
            Powered by PazeX
          </p>
        </div>
      </div>
    </>
  );
};

export default TripTracking;
